import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ApiClient, MfaSetup, SignInAnswer } from '@despachador/api-client';
import { Notice, Spinner } from '../index';
import { AuthCard, AuthenticatorSetup, authMessage, CodeInput, errorCode, type AuthBrand } from './pieces';
import { MOTIVE_OF_CODE, type StatusMotive } from './StatusScreen';

export type Challenge = Extract<SignInAnswer, { status: 'CHALLENGE' }>;
type SignedIn = Extract<SignInAnswer, { status: 'SIGNED_IN' }>;

/**
 * The step after the password (`/acceso/verificacion`): the code of the authenticator app (or a
 * recovery code), or — when the company demands it and the account has none — setting the app up,
 * which ends by handing over the recovery codes once.
 */
export function ChallengeStep({
  api,
  brand,
  challenge,
  large,
  onSignedIn,
  onExpired,
  onRefused,
  onCancel,
}: {
  api: ApiClient;
  brand: AuthBrand;
  challenge: Challenge;
  large?: boolean;
  onSignedIn: (answer: SignedIn) => void;
  /** The challenge died (too slow, too many attempts, another browser): back to the start. */
  onExpired: (message: string) => void;
  onRefused: (motive: StatusMotive) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [setup, setSetup] = useState<MfaSetup | null>(challenge.setup ?? null);
  const [error, setError] = useState<string | null>(null);
  /** The second factor cannot be set up here (only inside an invitation): no setup is offered. */
  const [blocked, setBlocked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const btn = `btn primary block ${large ? 'lg' : ''}`;

  const fail = (err: unknown) => {
    const code = errorCode(err);
    const motive = MOTIVE_OF_CODE[code ?? ''];
    if (code === 'MFA_ENROLLMENT_BLOCKED') setBlocked(authMessage(err));
    else if (code === 'CHALLENGE_EXPIRED') onExpired(authMessage(err));
    else if (motive) onRefused(motive);
    else setError(authMessage(err));
  };

  // the mandatory setup starts once per challenge (StrictMode runs effects twice: a second start
  // would replace the secret the screen shows)
  const started = useRef<string | null>(null);
  useEffect(() => {
    if (challenge.step !== 'MFA_SETUP' || setup || started.current === challenge.challenge) return;
    started.current = challenge.challenge;
    api.loginStep({ challenge: challenge.challenge, action: 'start' }).then((r) => (r.status === 'CHALLENGE' && r.setup ? setSetup(r.setup) : setError('No se pudo preparar la verificación. Empieza de nuevo.')), fail);
  }, [challenge.challenge]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const input =
        challenge.step === 'MFA_SETUP'
          ? ({ challenge: challenge.challenge, action: 'confirm', code: value } as const)
          : recovery
            ? { challenge: challenge.challenge, recoveryCode: value.trim() }
            : { challenge: challenge.challenge, code: value };
      const r = await api.loginStep(input);
      if (r.status === 'SIGNED_IN') onSignedIn(r);
      else setError('Falta un paso más. Empieza de nuevo.');
    } catch (err) {
      setValue('');
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const cancel = (
    <button type="button" className="btn ghost sm" onClick={onCancel}>
      <ArrowLeft size={14} aria-hidden /> Ingresar con otra cuenta
    </button>
  );
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit();
  };

  if (challenge.step === 'MFA')
    return (
      <AuthCard brand={brand} title="Verificación en 2 pasos" error={error} onSubmit={onSubmit}>
        <CodeInput value={value} onChange={setValue} recovery={recovery} autoFocus />
        <button className={btn} disabled={busy}>
          {busy ? 'Verificando…' : 'Verificar e ingresar'}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => {
            setRecovery(!recovery);
            setValue('');
            setError(null);
          }}
        >
          {recovery ? 'Usar el código de la app' : '¿No tienes el teléfono? Usa un código de recuperación'}
        </button>
        <p className="small muted" style={{ margin: 0 }}>
          ¿Perdiste el teléfono y los códigos? Pide ayuda al administrador de tu empresa.
        </p>
        {cancel}
      </AuthCard>
    );

  if (blocked)
    return (
      <AuthCard brand={brand} title="Verificación en 2 pasos">
        <Notice tone="warn" urgent>
          {blocked}
        </Notice>
        {cancel}
      </AuthCard>
    );

  return (
    <AuthCard brand={brand} title="Configura la verificación en 2 pasos" error={error} onSubmit={onSubmit}>
      <Notice tone="info">Tu cuenta necesita un segundo paso al ingresar: un código que cambia cada 30 segundos en tu teléfono.</Notice>
      {setup ? <AuthenticatorSetup {...setup} /> : !error && <Spinner block label="Preparando" />}
      <CodeInput value={value} onChange={setValue} />
      <button className={btn} disabled={busy || !setup}>
        {busy ? 'Verificando…' : 'Activar e ingresar'}
      </button>
      {cancel}
    </AuthCard>
  );
}
